import { OpenStreetMapProvider } from 'leaflet-geosearch'

// Geocoding helpers shared by the LocationPicker and the forms that wire
// it up. Both directions ride OpenStreetMap / Nominatim — the same free,
// key-less service the in-map search box already uses — so there's no new
// dependency or API credential to manage.

/** Structured address pulled out of a reverse-geocode result. */
export interface ParsedAddress {
  country?: string;
  state?: string;
  district?: string;
  taluka?: string;
  pincode?: string;
  /** Full human-readable address (Nominatim `display_name`). */
  label?: string;
}

export interface GeocodeHit {
  latitude: number;
  longitude: number;
  label: string;
}

export interface ParsedCoords {
  lat: number;
  lng: number;
}

/**
 * Parse a user-pasted location into `{ lat, lng }`. Built for the case where
 * a place exists on Google Maps but not in Ola's POI search — the user copies
 * the coordinate (right-click → "copy coordinates") or the page URL from
 * Google and pastes it here.
 *
 * Accepts, in priority order:
 *   1. Google "place" coordinate embedded in the URL: `!3d<lat>!4d<lng>`
 *      (this is the actual pinned place, more accurate than the map centre)
 *   2. Google map-centre in the URL: `@<lat>,<lng>`
 *   3. URL query forms: `?q=<lat>,<lng>` / `ll=` / `query=` / `destination=`
 *   4. A bare `"<lat>, <lng>"` pair (comma- or space-separated)
 *
 * Returns `null` when nothing valid / in-range (lat ±90, lng ±180) is found.
 */
export const parseCoordinates = (input: string): ParsedCoords | null => {
  const text = (input || '').trim()
  if (!text) return null

  const build = (lat: number, lng: number): ParsedCoords | null =>
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
      ? { lat, lng }
      : null

  const place = text.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/)
  if (place) return build(Number(place[1]), Number(place[2]))

  const at = text.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/)
  if (at) return build(Number(at[1]), Number(at[2]))

  const query = text.match(
    /[?&](?:q|ll|query|destination)=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/
  )
  if (query) return build(Number(query[1]), Number(query[2]))

  // Bare pair — anchored so an address that merely contains digits doesn't
  // get mistaken for a coordinate.
  const pair = text.match(/^(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)$/)
  if (pair) return build(Number(pair[1]), Number(pair[2]))

  return null
}

/**
 * Pull the human-readable place NAME out of a pasted Google Maps URL — the
 * `/place/<name>/` segment (e.g. `…/maps/place/Oceano/@…` → "Oceano"). This
 * recovers the brand/landmark name the user recognises even when the place
 * isn't in any reverse-geocoder's database. Returns `null` when the input
 * isn't a Google place URL or the segment is just coordinates.
 */
export const parsePlaceName = (input: string): string | null => {
  const text = (input || '').trim()
  const m = text.match(/\/place\/([^/@?]+)/)
  if (!m) return null
  try {
    const name = decodeURIComponent(m[1].replace(/\+/g, ' '))
      .replace(/\s+/g, ' ')
      .trim()
    if (!name) return null
    // Google sometimes uses `/place/<lat>,<lng>` — that's not a name.
    if (/^-?\d+(?:\.\d+)?,\s*-?\d+(?:\.\d+)?$/.test(name)) return null
    return name
  } catch {
    return null
  }
}

/**
 * Reverse geocode via Ola Maps — better coverage for Indian addresses than
 * Nominatim (which mis-locates rural points). Returns a single formatted
 * address string, or `null` when no key is set / Ola finds nothing / the
 * call fails (callers fall back to {@link reverseGeocode}).
 */
export const olaReverseGeocode = async (
  latitude: number,
  longitude: number
): Promise<string | null> => {
  const apiKey = process.env.REACT_APP_OLA_MAPS_KEY
  if (!apiKey) return null
  try {
    const url =
      'https://api.olamaps.io/places/v1/reverse-geocode' +
      `?latlng=${latitude},${longitude}&api_key=${apiKey}`
    const resp = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!resp.ok) return null
    const data = await resp.json()
    const first = data?.results?.[0]
    const address: string | undefined =
      first?.formatted_address ?? first?.name
    return address && address.trim() ? address.trim() : null
  } catch {
    return null
  }
}

/**
 * Structured reverse geocode via Ola Maps — same call as
 * {@link olaReverseGeocode} but parses `address_components` into our
 * {@link ParsedAddress} shape (country / state / district / taluka / pincode +
 * a formatted `label`). Ola's components are India-accurate, so this is the
 * primary source for auto-filling the form's Pincode (and other) fields.
 * Returns `null` when no key / no result / the call fails.
 */
export const olaReverseGeocodeDetailed = async (
  latitude: number,
  longitude: number
): Promise<ParsedAddress | null> => {
  const apiKey = process.env.REACT_APP_OLA_MAPS_KEY
  if (!apiKey) return null
  try {
    const url =
      'https://api.olamaps.io/places/v1/reverse-geocode' +
      `?latlng=${latitude},${longitude}&api_key=${apiKey}`
    const resp = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!resp.ok) return null
    const data = await resp.json()
    const first = data?.results?.[0]
    if (!first) return null

    const comps: Array<{ types?: string[]; long_name?: string }> =
      first.address_components ?? []
    const pick = (type: string): string | undefined =>
      comps.find((c) => Array.isArray(c.types) && c.types.includes(type))
        ?.long_name

    const district = pick('administrative_area_level_2')
    let taluka = pick('administrative_area_level_3') ?? pick('locality')
    // Never echo the same value into both district and taluka.
    if (taluka && district && taluka === district) taluka = undefined

    return {
      country: pick('country'),
      state: pick('administrative_area_level_1'),
      district,
      taluka,
      pincode: pick('postal_code'),
      label: first.formatted_address ?? first.name ?? undefined
    }
  } catch {
    return null
  }
}

// One shared provider instance — leaflet-geosearch caches nothing itself,
// but reusing the instance avoids re-allocating per call.
const provider = new OpenStreetMapProvider()

/**
 * Forward geocode — turn a free-text address into coordinates. Returns the
 * top match, or `null` when Nominatim finds nothing / the call fails.
 */
export const forwardGeocode = async (
  query: string
): Promise<GeocodeHit | null> => {
  const q = query.trim()
  if (!q) return null
  try {
    const results = await provider.search({ query: q })
    if (!results.length) return null
    const { x, y, label } = results[0]
    return { latitude: y, longitude: x, label }
  } catch {
    return null
  }
}

/**
 * Forward geocode via Ola Maps — turn a free-text address into coordinates,
 * with better Indian-address coverage than Nominatim. Returns the top match,
 * or `null` when no key / nothing found / the call fails (callers fall back
 * to {@link forwardGeocode}).
 */
export const olaForwardGeocode = async (
  query: string
): Promise<GeocodeHit | null> => {
  const apiKey = process.env.REACT_APP_OLA_MAPS_KEY
  const q = query.trim()
  if (!q || !apiKey) return null
  try {
    const url =
      'https://api.olamaps.io/places/v1/geocode' +
      `?address=${encodeURIComponent(q)}&api_key=${apiKey}`
    const resp = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!resp.ok) return null
    const data = await resp.json()
    const results = data?.geocodingResults ?? data?.results ?? []
    const top = results[0]
    const loc = top?.geometry?.location
    if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') {
      return null
    }
    return {
      latitude: loc.lat,
      longitude: loc.lng,
      label: top.formatted_address ?? q
    }
  } catch {
    return null
  }
}

/**
 * Reverse geocode — turn coordinates into a structured address. We hit the
 * Nominatim `reverse` endpoint directly (the leaflet-geosearch provider only
 * exposes forward search) and map its `address` object onto our fields.
 *
 * Indian admin levels don't line up 1:1 with OSM's tag names, so the
 * district / taluka mapping is best-effort: we read the most specific tag
 * available and de-dupe so a single OSM value never lands in both slots.
 */
export const reverseGeocode = async (
  latitude: number,
  longitude: number
): Promise<ParsedAddress | null> => {
  try {
    const url =
      'https://nominatim.openstreetmap.org/reverse?format=jsonv2' +
      `&lat=${latitude}&lon=${longitude}&addressdetails=1&accept-language=en`
    const resp = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!resp.ok) return null
    const data = await resp.json()
    const a = data?.address ?? {}

    const district: string | undefined =
      a.state_district ?? a.district ?? a.county
    let taluka: string | undefined =
      a.county ??
      a.municipality ??
      a.city_district ??
      a.town ??
      a.suburb ??
      a.village ??
      a.city
    // Never echo the same value into both district and taluka.
    if (taluka && district && taluka === district) taluka = undefined

    return {
      country: a.country,
      state: a.state,
      district,
      taluka,
      pincode: a.postcode,
      label: data?.display_name
    }
  } catch {
    return null
  }
}
